/**
 * Custom hook for managing user mutations with error handling.
 *
 * Provides a unified interface for create, update, and delete operations
 * with consistent error handling and success notifications.
 */

import { useDeleteUser, useCreateUser, useUpdateUser } from './useUsers';
import { useErrorHandler } from '@/hooks';
import { transformCreateUserFormData, transformUpdateUserFormData } from '../utils/userFormTransformers';
import type { UserResponse } from '@/lib/api';
import type { CreateUserFormData, UpdateUserFormData } from '../validation/userFormSchemas';

/**
 * Hook return type for user mutations.
 */
export interface UseUserMutationsReturn {
  /**
   * Create a new user.
   */
  createUser: (formData: CreateUserFormData) => Promise<void>;
  /**
   * Update an existing user.
   */
  updateUser: (userId: string, formData: UpdateUserFormData) => Promise<void>;
  /**
   * Delete a user.
   */
  deleteUser: (user: UserResponse) => Promise<void>;
  /**
   * Whether any mutation is currently in progress.
   */
  isMutating: boolean;
}

/**
 * Custom hook that wraps user mutations with error handling and notifications.
 */
export function useUserMutations(): UseUserMutationsReturn {
  const deleteUserMutation = useDeleteUser();
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const { handleError, handleSuccess } = useErrorHandler('User Management');

  const createUser = async (formData: CreateUserFormData) => {
    try {
      const createData = transformCreateUserFormData(formData);
      await createUserMutation.mutateAsync(createData);
      handleSuccess(`User ${formData.email} created successfully`);
    } catch (err) {
      handleError(err);
      throw err; // Re-throw to allow caller to handle if needed
    }
  };

  const updateUser = async (userId: string, formData: UpdateUserFormData) => {
    try {
      const updateData = transformUpdateUserFormData(formData);
      await updateUserMutation.mutateAsync({ userId, data: updateData });
      handleSuccess(`User ${formData.email} updated successfully`);
    } catch (err) {
      handleError(err);
      throw err; // Re-throw to allow caller to handle if needed
    }
  };

  const deleteUser = async (user: UserResponse) => {
    try {
      await deleteUserMutation.mutateAsync(user.id);
      handleSuccess(`User ${user.email} deleted successfully`);
    } catch (err) {
      handleError(err);
      throw err; // Re-throw to allow caller to handle if needed
    }
  };

  const isMutating = createUserMutation.isPending || updateUserMutation.isPending || deleteUserMutation.isPending;

  return {
    createUser,
    updateUser,
    deleteUser,
    isMutating,
  };
}
